import { createTheme, Icon } from "@mui/material";
import {
  CheckOutlined,
  PriorityHighRounded,
  CancelOutlined,
} from "@mui/icons-material";

const palette = {
  primary: {
    main: "#000000",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#ffffff",
    contrastText: "#000000",
  },
  success: {
    main: "#209C33",
  },
  info: {
    main: "#000000",
  },
  warning: {
    main: "#FF7F07",
  },
  error: {
    main: "#FF4C4C",
  },
  delete: {
    main: "#EB5757",
  },
  text: {
    primary: "#000000",
    secondary: "#333339",
    tertiary: "#D9D9D9",
    quaternary: "#909090",
  },
  paper: {
    primary: "#ffffff",
    secondary: "#f5f5f5",
  },
  icon: {
    primary: "#1C1B1F",
  },
  overlay: {
    white05: "rgba(255,255,255,0.05)",
    white08: "rgba(255,255,255,0.08)",
    white10: "rgba(255,255,255,0.1)",
    white15: "rgba(255,255,255,0.15)",
    white20: "rgba(255,255,255,0.2)",
    white30: "rgba(255,255,255,0.3)",
    white40: "rgba(255,255,255,0.4)",
    white50: "rgba(255,255,255,0.5)",
    white60: "rgba(255,255,255,0.6)",
    white70: "rgba(255,255,255,0.7)",
    white80: "rgba(255,255,255,0.8)",
    white90: "rgba(255,255,255,0.9)",
    white95: "rgba(255,255,255,0.95)",
    white98: "rgba(255,255,255,0.98)",
    black03: "rgba(0,0,0,0.03)",
    black04: "rgba(0,0,0,0.04)",
    black05: "rgba(0,0,0,0.05)",
    black06: "rgba(0,0,0,0.06)",
    black08: "rgba(0,0,0,0.08)",
    black10: "rgba(0,0,0,0.1)",
    black15: "rgba(0,0,0,0.15)",
    black20: "rgba(0,0,0,0.2)",
    black30: "rgba(0,0,0,0.3)",
    black40: "rgba(0,0,0,0.4)",
    black60: "rgba(0,0,0,0.6)",
    black70: "rgba(0,0,0,0.7)",
    black80: "rgba(0,0,0,0.8)",
  },
  topSectionBg: "#141c24",
  darkBg: "#1a1a1a",
  tableHeaderBg: "#fafbfc",
  smokyWhite: "#f8f9fa",
};

const MuiTabs = {
  styleOverrides: {
    root: {
      marginBottom: 3,
      position: "sticky",
      zIndex: 3,
      backgroundColor: palette.secondary.main,
      transition: "inset 0.1s linear",
      "& .MuiTabs-indicator": {
        height: "1px !important",
      },
      top: "0px",
      "@media (max-width:960px)": {
        marginLeft: "-1rem",
        marginRight: "-1rem",
      },
      "@media (min-width:960px)": {
        marginLeft: "-2rem",
        marginRight: "-2rem",
      },
    },
  },
};

const MuiTab = {
  styleOverrides: {
    root: {
      color: palette.text.secondary,
      textTransform: "uppercase",
      fontSize: "12px !important",
      lineHeight: "31.92px !important",
      fontWeight: "700 !important",
      letterSpacing: "1px !important",
      paddingBottom: "8px !important",
      "&.Mui-selected": {
        color: palette.text.primary,
      },
      borderBottom: `1px solid ${palette.text.tertiary}`,
    },
  },
};

const MuiButton = {
  styleOverrides: {
    root: {
      "&.Mui-disabled": {
        color: `${palette.text.disabled} !important`,
      },
      minWidth: "auto",
      textWrap: "nowrap",
    },
  },
  variants: [
    {
      props: { variant: "contained" },
      style: {
        borderRadius: "30px",
        padding: "10px 16px",
      },
    },
    {
      props: { variant: "outlined" },
      style: {
        borderRadius: "30px",
        padding: "10px 16px",
        border: `1px solid ${palette.text.tertiary}`,
      },
    },
    {
      props: { variant: "outlined", size: "grown" },
      style: {
        borderRadius: "30px",
        padding: "10px 24px !important",
        border: `1px solid ${palette.text.tertiary}`,
      },
    },
    {
      props: { variant: "contained", size: "shrinked" },
      style: {
        borderRadius: "30px",
        padding: "6px 16px !important",
      },
    },
    {
      props: { variant: "outlined", size: "shrinked" },
      style: {
        borderRadius: "30px",
        padding: "6px 16px !important",
        border: `1px solid ${palette.text.tertiary}`,
      },
    },
    {
      props: { variant: "contained", size: "small" },
      style: {
        borderRadius: "15px",
        padding: "8px 10px !important",
        fontSize: "10px !important",
        lineHeight: "1 !important",
        "@media (max-width:960px)": {
          maxHeight: "23px",
        },
      },
    },
    {
      props: { variant: "outlined", size: "small" },
      style: {
        borderRadius: "15px",
        padding: "8px 10px !important",
        border: `1px solid ${palette.text.tertiary}`,
        fontSize: "10px !important",
        lineHeight: "1 !important",
      },
    },
    {
      props: { variant: "outlined", size: "micro" },
      style: {
        padding: "4px 6px !important",
        fontSize: "10px !important",
        lineHeight: "1 !important",
      },
    },
    {
      props: { variant: "outlined-squared" },
      style: {
        borderRadius: "4px",
        padding: "8px 24px !important",
        border: `1px solid ${palette.text.tertiary}`,
      },
    },
  ],
};

const MuiIconButton = {
  styleOverrides: {
    root: {
      color: `${palette.icon.primary}`,
      "& span": {
        verticalAlign: "middle",
      },
    },
  },
  variants: [
    {
      props: { variant: "contained" },
      style: {
        padding: "8px",
        backgroundColor: palette.primary.main,
        "&:hover": {
          backgroundColor: palette.primary.main,
        },
      },
    },
    {
      props: { variant: "outlined" },
      style: {
        padding: "8px",
        backgroundColor: palette.secondary.main,
        border: `1px solid ${palette.text.tertiary}`,
      },
    },
  ],
};

const MuiChip = {
  styleOverrides: {
    root: {
      fontSize: "12px",
      lineHeight: "16.2px",
      fontWeight: 400,
    },
  },
  variants: [
    {
      props: { variant: "default" },
      style: {
        height: "auto",
        borderRadius: "19px",
        padding: "3.8px",
        border: `1px solid transparent`,
      },
    },
    {
      props: { variant: "filled" },
      style: {
        height: "auto",
        borderRadius: "19px",
        padding: "3.8px",
      },
    },
    {
      props: { variant: "outlined" },
      style: {
        height: "auto",
        borderRadius: "19px",
        padding: "3.8px",
        borderColor: palette.text.tertiary,
      },
    },
  ],
};

const MuiAlert = {
  styleOverrides: {
    root: {
      fontSize: "14px",
      lineHeight: "18.9px",
      fontWeight: 400,
      borderRadius: "34px",
    },
  },
  defaultProps: {
    iconMapping: {
      success: <CheckOutlined fontSize="inherit" />,
      info: (
        <Icon baseClassName="material-symbols-outlined">import_contacts</Icon>
      ),
      warning: <PriorityHighRounded fontSize="inherit" />,
      error: <CancelOutlined fontSize="inherit" />,
    },
  },
};

const MuiAvatar = {
  variants: [
    {
      props: { size: "small" },
      style: {
        width: "20px",
        height: "20px",
        fontSize: "16px",
      },
    },
  ],
};

const MuiTextField = {
  variants: [
    {
      props: { type: "search" },
      style: {
        borderRadius: "29px",
        border: `1px solid ${palette.text.tertiary}`,
        "& .MuiInputBase-input": {
          padding: "10px",
          fontSize: "14px",
        },
        "& fieldset": {
          display: "none",
        },
      },
    },
    {
      props: { variant: "standard" },
      style: {
        "& .MuiInputBase-root:before": {
          borderBottom: "1px solid #D9D9D9",
        },
      },
    },
  ],
};

export const Theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600, // mobile
      md: 960, // tablet
      lg: 1280, // desktop
      xl: 1920, // large desktop
    },
  },
  palette,
  shadows: {
    ...createTheme().shadows,
    cardSm: (palette, mode = "light") =>
      mode === "dark"
        ? `0 4px 12px rgba(0,0,0,0.3), 0 0 0 1px ${palette.overlay.white10}`
        : "0 4px 12px rgba(0,0,0,0.15)",
    cardMd: (palette) => `0 2px 20px ${palette.overlay.black08}`,
    cardLg: (palette, mode = "light") =>
      mode === "dark"
        ? `0 8px 40px ${palette.overlay.black40}`
        : `0 8px 40px ${palette.overlay.black06}`,
    tooltip: (palette) => `0 8px 24px ${palette.overlay.black40}`,
    notification: (palette) => `0 4px 12px ${palette.overlay.black20}`,
  },
  gradients: {
    ctaLight: (palette) => "linear-gradient(135deg, #fafbfc 0%, #f5f6f8 100%)",
    ctaDark: (palette) =>
      `linear-gradient(135deg, ${palette.primary.main} 0%, #1a1a1a 100%)`,
    ctaBorderLight: (palette) =>
      `linear-gradient(90deg, transparent 0%, ${palette.black05} 50%, transparent 100%)`,
    ctaBorderDark: (palette) =>
      `linear-gradient(90deg, transparent 0%, ${palette.overlay.white20} 50%, transparent 100%)`,
  },
  components: {
    MuiTabs,
    MuiTab,
    MuiButton,
    MuiIconButton,
    MuiChip,
    MuiAlert,
    MuiAvatar,
    MuiTextField,
  },
  typography: {
    fontFamily: ["Satoshi", "Nunito", "Open Sans", "Arial"].join(","),
    h1: {
      "@media (max-width:960px)": {
        fontSize: "54px",
        lineHeight: "63px",
        fontWeight: 700,
        letterSpacing: "-0.4px",
      },
      "@media (min-width:960px)": {
        fontSize: "4rem",
        lineHeight: "1",
        fontWeight: 600,
      },
    },
    h2: {
      // Heading
      fontSize: "20px",
      lineHeight: "27px",
      fontWeight: 700,
      letterSpacing: "-0.4px",
    },
    title: {
      fontSize: "24px",
      lineHeight: "32.4px",
      fontWeight: 600,
      letterSpacing: "-0.4px",
      fontFamily: "Satoshi",
    },
    h5: {
      // Body Medium
      fontSize: "16px",
      lineHeight: "21.6px",
      fontWeight: 500,
    },
    body1: {
      // Body Regular
      fontSize: "16px",
      lineHeight: "21.6px",
      fontWeight: 400,
      // '@media (min-width:960px)': {
      //   fontSize: '1rem',
      //   lineHeight: '1.5',
      //   fontWeight: 400,
      // },
    },
    subtitle1: {
      // Subtitle Medium
      fontSize: "14px",
      lineHeight: "18.9px",
      fontWeight: 500,
      // '@media (min-width:960px)': {
      //   fontSize: '1rem',
      //   lineHeight: '1.5',
      //   fontWeight: 500,
      // },
    },
    subtitle2: {
      // Subtitle Regular
      fontSize: "14px",
      lineHeight: "18.9px",
      fontWeight: 400,
      // '@media (min-width:960px)': {
      //   fontSize: '1rem',
      //   lineHeight: '1.5',
      //   fontWeight: 400,
      // },
    },
    button: {
      textTransform: "uppercase",
      fontSize: "14px",
      lineHeight: "24px",
      fontWeight: 500,
      letterSpacing: "0.4px",
      // '@media (min-width:960px)': {
      //   fontSize: '0.875rem',
      //   lineHeight: '1.75',
      //   fontWeight: 500,
      // },
    },
    caption: {
      fontSize: "12px",
      lineHeight: "19.92px",
      fontWeight: 400,
      letterSpacing: "0.4px",
      // '@media (min-width:960px)': {
      //   fontSize: '0.8125rem',
      //   lineHeight: '1.66',
      //   fontWeight: 400,
      // },
      "&.MuiFormHelperText-root": {
        color: `${palette.text.secondary} !important`,
      },
    },
    footer: {
      color: "#979898 !important",
      fontSize: "12px",
      lineHeight: "1.5",
      fontWeight: 400,
      letterSpacing: "-0.4px",
      // '@media (min-width:960px)': {
      //   fontSize: '0.875rem',
      //   lineHeight: '1.43',
      //   fontWeight: 400,
      // },
    },
  },
  clampLines: (lines) => ({
    WebkitBoxOrient: "vertical",
    display: "-webkit-box",
    WebkitLineClamp: lines,
    overflow: "hidden",
  }),
  ellipsis: () => ({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
});
